import { useState } from 'react';
import {
  CalendarBlank,
  ChatCircle,
  Drop,
  Play,
  ShieldCheck,
  WarningOctagon,
  Warning,
  InstagramLogo,
  ArrowSquareOut,
  CaretDown,
} from '../../../../design-system/icons';
import { categoryTone } from '../shared';

/* ─── IgPostCard ─────────────────────────────────────────────────────────────
 * Instagram Recent Post card (Figma 2019-69865): 112px square thumbnail with
 * play + duration, 24px title, a bordered status pill (Safe Content / Use With
 * Caution / High Risk) with #171717 text, meta pills (date · comments ·
 * toxicity), sentiment dots, and a footer (View reel + Details). Fields the IG
 * payload may not send (thumbnail, caption, date, sentiment) degrade
 * gracefully. */

interface IgFlag {
  category?: string;
  value?: string;
  source?: string;
}

const RISK_TONE = categoryTone(0);

function flagLabel(f: IgFlag): string {
  const cat = f.category?.replace(/_/g, ' ') ?? f.source ?? 'Flag';
  return cat.replace(/\b\w/g, (c) => c.toUpperCase());
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v : undefined;
}
function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/** Status pill — bordered, tinted background, #171717 label (Figma). */
function statusFor(score: number) {
  if (score >= 70)
    return {
      label: 'Safe Content',
      iconColor: '#059669',
      bg: 'rgba(5,150,105,0.12)',
      border: '#D1FAE5',
      Icon: ShieldCheck,
    };
  if (score >= 45)
    return {
      label: 'Use With Caution',
      iconColor: '#CA8A04',
      bg: 'rgba(234,179,8,0.10)',
      border: '#FEF3C7',
      Icon: WarningOctagon,
    };
  return {
    label: 'High Risk',
    iconColor: '#DC2626',
    bg: 'rgba(220,38,38,0.12)',
    border: '#FEE2E2',
    Icon: Warning,
  };
}

function fmtDate(raw: string | number | undefined): string {
  if (raw == null) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function IgPostCard({ post, index }: { post: Record<string, unknown>; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const score = num(post.brand_safety_score) ?? 0;
  const flags = (post.brand_safety_flags as IgFlag[] | undefined) ?? [];
  const toxicity = num(post.comment_toxicity_score) ?? 0;
  const comments = num(post.comments_analyzed) ?? 0;
  const locator = str(post.locator);

  const thumbnail =
    str(post.thumbnail) ?? str(post.thumbnail_url) ?? str(post.media_url) ?? str(post.display_url) ?? str(post.image);
  const title = str(post.title) ?? str(post.caption) ?? str(post.text) ?? `Post ${index + 1}`;
  const dateLabel = fmtDate((post.timestamp ?? post.published_at ?? post.taken_at ?? post.date) as string | number | undefined);
  const duration = str(post.duration);

  const pos = num(post.positive_percentage);
  const neu = num(post.neutral_percentage);
  const neg = num(post.negative_percentage);
  const hasSentiment = pos != null || neu != null || neg != null;

  const igUrl = str(post.permalink) ?? (locator ? `https://www.instagram.com/p/${locator}/` : null);
  const status = statusFor(score);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-start gap-4 py-4 pr-4 pl-[18px]">
        {/* Thumbnail — 112px square */}
        <div className="relative size-28 shrink-0 overflow-hidden rounded-xl bg-stone-900">
          {thumbnail ? (
            <img src={thumbnail} alt="" className="size-full object-cover" />
          ) : (
            <div className="flex size-full items-center justify-center bg-[linear-gradient(135deg,#FEDA75,#FA7E1E,#D62976,#962FBF,#4F5BD5)]">
              <InstagramLogo size={26} color="#fff" />
            </div>
          )}
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex size-11 items-center justify-center rounded-full bg-white text-stone-900">
              <Play size={20} weight="fill" />
            </span>
          </span>
          {duration && (
            <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 rounded-full bg-[#18181B] px-[7px] py-0.5 text-caption-regular/4 font-medium text-white">
              {duration}
            </span>
          )}
        </div>

        {/* Info */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1 truncate text-heading-h5/8 font-medium text-[#171717]">
              {title}
            </div>
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-1 text-caption-medium text-[#171717]"
              style={{ background: status.bg, borderColor: status.border }}
            >
              <status.Icon size={14} weight="fill" color={status.iconColor} />
              {status.label}
            </span>
          </div>

          {/* Meta pills */}
          <div className="flex flex-wrap items-center gap-2">
            {dateLabel && <MetaChip icon={<CalendarBlank size={16} />}>{dateLabel}</MetaChip>}
            <MetaChip icon={<ChatCircle size={16} />}>{comments.toLocaleString()} comments</MetaChip>
            <MetaChip icon={<Drop size={16} />}>{toxicity.toFixed(0)}% toxicity</MetaChip>
          </div>

          {/* Sentiment dots (only when the payload carries a breakdown) */}
          {hasSentiment && (
            <div className="flex flex-wrap items-center gap-[18px] pt-0.5 text-body-sm-medium text-[#171717]">
              <SentDot color="#16A34A" label={`${(pos ?? 0).toFixed(0)}% positive`} />
              <SentDot color="#EAB308" label={`${(neu ?? 0).toFixed(0)}% neutral`} />
              <SentDot color="#DC2626" label={`${(neg ?? 0).toFixed(0)}% negative`} />
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-border px-6 py-3.5">
        {igUrl ? (
          <a
            href={igUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 text-body-md-medium text-[#171717] no-underline hover:opacity-80"
          >
            <InstagramLogo size={18} /> View reel on Instagram <ArrowSquareOut size={18} />
          </a>
        ) : (
          <span />
        )}
        {flags.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="inline-flex items-center gap-1.5 rounded-[9px] px-2 py-1.5 text-body-sm-medium text-(--brand-600)"
          >
            {expanded ? 'Less' : 'Details'}
            <CaretDown
              size={13}
              className={expanded ? 'rotate-180 transition-transform' : 'transition-transform'}
            />
          </button>
        )}
      </div>

      {/* Expanded flags */}
      {expanded && flags.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t border-border bg-muted p-5">
          {flags.map((f, fi) => (
            <span
              key={fi}
              className="inline-flex items-center gap-2 rounded-full bg-card px-3 py-1.5 text-body-sm-medium text-[#404040]"
              style={{ border: `1px solid ${RISK_TONE.dot}33` }}
            >
              <span className="size-[7px] rounded-full" style={{ background: RISK_TONE.dot }} />
              {flagLabel(f)}
              {f.value && <span className="text-muted-foreground">· {f.value}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function MetaChip({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted py-[5px] pr-3 pl-2.5 text-body-sm-regular text-[#404040]">
      {icon}
      {children}
    </span>
  );
}

function SentDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-[7px]">
      <span className="size-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}
